import { useMemo } from 'react';
import { registerQuery, useQuery } from 'data-fetching-client';
import { createRpcClientV2 } from 'rpc-client-utils';
import http from 'hub-http/clients/apiClient';
import { resolveBreezeAgentNamesRpc } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsMetadataRpc';
import { captureException } from '../utils/sentryUtils';
const chirpClient = createRpcClientV2({
  hubHttpClient: http
});
const BREEZE_AGENTS_FIELD = 'breezeAgents';
const EMPTY_AGENTS_RECORD = {};

// Keep this in sync with ResolveBreezeAgentNamesRequestIF.MAX_AGENT_IDS. An
// approval template can contain more agents than an individual requester flow.
export const MAX_BREEZE_AGENT_IDS_PER_REQUEST = 50;
const normalizeAgentIds = agentIds => [...new Set(agentIds)].sort();
const getAgentIdBatches = agentIds => {
  const batches = [];
  for (let startIndex = 0; startIndex < agentIds.length; startIndex += MAX_BREEZE_AGENT_IDS_PER_REQUEST) {
    batches.push(agentIds.slice(startIndex, startIndex + MAX_BREEZE_AGENT_IDS_PER_REQUEST));
  }
  return batches;
};
const FETCH_BREEZE_AGENTS = registerQuery({
  fieldName: BREEZE_AGENTS_FIELD,
  args: ['agentIds'],
  fetcher: async ({
    agentIds
  }) => {
    const requestedAgentIds = new Set(agentIds);
    const responses = await Promise.all(getAgentIdBatches(agentIds).map(async agentIdBatch => {
      try {
        return await chirpClient.call(resolveBreezeAgentNamesRpc, {
          request: {
            agentIds: agentIdBatch
          }
        });
      } catch (err) {
        captureException(err, {
          extra: {
            fingerprint: 'useBreezeAgents-resolveBreezeAgentNames',
            agentIds: agentIdBatch
          }
        });
        return undefined;
      }
    }));
    const agentsRecord = {};
    for (const response of responses) {
      for (const agent of (_response$agents = response === null || response === void 0 ? void 0 : response.agents) !== null && _response$agents !== void 0 ? _response$agents : []) {
        var _response$agents, _agent$displayName;
        const displayName = (_agent$displayName = agent.displayName) === null || _agent$displayName === void 0 ? void 0 : _agent$displayName.trim();
        // The server echoes the requested ID. Still restrict the record to
        // this query's IDs so an unexpected response cannot affect another
        // approver's display name.
        if (requestedAgentIds.has(agent.agentId) && displayName) {
          agentsRecord[agent.agentId] = {
            displayName
          };
        }
      }
    }
    return agentsRecord;
  }
});
export const useBreezeAgents = ({
  agentIds
}) => {
  var _data$BREEZE_AGENTS_F;
  // The DFC query key must not depend on display order or duplicate actors.
  // This also means each unique agent is requested only once per view.
  const normalizedAgentIds = useMemo(() => normalizeAgentIds(agentIds), [agentIds]);
  const {
    data,
    loading
  } = useQuery(FETCH_BREEZE_AGENTS, {
    variables: {
      agentIds: normalizedAgentIds
    },
    skip: normalizedAgentIds.length === 0
  });
  return {
    agentsRecord: (_data$BREEZE_AGENTS_F = data === null || data === void 0 ? void 0 : data[BREEZE_AGENTS_FIELD]) !== null && _data$BREEZE_AGENTS_F !== void 0 ? _data$BREEZE_AGENTS_F : EMPTY_AGENTS_RECORD,
    loading
  };
};